import { BrowserUsePanel, DeepResearchPanel, DocumentReaderPanel, LegalDraftPanel, BilingualPanel, VoicePanel, AgentPanel, MemoryPanel, MarketingPanel } from "./ProductPanels";
/**
 * MoonshotVoiceChat — self-contained chat UI for the moonshot demo.
 *
 * This component implements PRE-158 (voice I/O chat) without touching the
 * production GlobalChatSystem tree, so the non-moonshot chat UX is guaranteed
 * to be unchanged.
 *
 * Features:
 *   - Mic button (lucide-react Mic) right-aligned inside the input bar
 *   - Gold pulse + glow when listening
 *   - Waveform SVG animation overlay while listening
 *   - Scripted transcription: words of the fixture user query stream into the
 *     input one at a time (120ms per word), then auto-submit
 *   - Assistant reply streams chunk-by-chunk from the active fixture
 *   - Assistant bubble includes a Volume2 speaker icon that plays
 *     `fixture.voiceReplyUrl` if present
 *   - Respects `?hud=off` for clean capture (no dev-chrome chatter)
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Mic, Volume2, Send } from 'lucide-react';
import { useDemoMock } from '../DemoMockProvider';
import { useTranscriptScroll } from '../../hooks/useTranscriptScroll';
import { journeyFixtures } from '../fixtures';

type Role = 'user' | 'assistant';

interface Message {
  id: string;
  role: Role;
  text: string;
  streaming?: boolean;
  voiceUrl?: string;
}

/* ------------------------------------------------------------------------- */
/* Styled bits — brand tokens: #C6A75E, #B6924C, #1A1A1A, #FAFAF8             */
/* ------------------------------------------------------------------------- */

const GOLD = 'var(--brand-gold)';
const GOLD_HOVER = 'var(--brand-gold-hover)';
const DARK = 'var(--brand-black)';
const LIGHT = 'var(--brand-canvas)';

const pulseGold = keyframes`
  0%   { transform: scale(1.0); box-shadow: 0 0 0 0 #C6A75E66; }
  50%  { transform: scale(1.15); box-shadow: 0 0 20px 4px #C6A75E66; }
  100% { transform: scale(1.0); box-shadow: 0 0 0 0 #C6A75E00; }
`;

const bar1 = keyframes`
  0%, 100% { height: 6px; }
  50%      { height: 18px; }
`;
const bar2 = keyframes`
  0%, 100% { height: 10px; }
  50%      { height: 22px; }
`;
const bar3 = keyframes`
  0%, 100% { height: 14px; }
  50%      { height: 26px; }
`;
const bar4 = keyframes`
  0%, 100% { height: 8px; }
  50%      { height: 20px; }
`;
const bar5 = keyframes`
  0%, 100% { height: 12px; }
  50%      { height: 24px; }
`;

const ChatShell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  flex-direction: row;
  background: ${LIGHT};
  color: ${DARK};
  font-family: "Inter", system-ui, sans-serif;
`;

const ChatHeader = styled.header`
  padding: 20px 32px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: baseline;
  gap: 16px;
  background: #fff;
`;

const ChatTitle = styled.h1`
  margin: 0;
  font-family: "Space Grotesk", system-ui, sans-serif;
  font-weight: 600;
  font-size: 20px;
  letter-spacing: -0.01em;
  color: ${DARK};
`;

const ChatSubtitle = styled.span`
  font-family: "Inter", system-ui, sans-serif;
  font-size: 13px;
  color: rgba(26, 26, 26, 0.6);
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 32px 32px 160px;
  display: flex;
  flex-direction: column;
`;

/**
 * Inner content sizer for the shared useTranscriptScroll controller's
 * ResizeObserver (2026-08 scroll redesign) — owns the inter-bubble gap.
 */
const MessagesContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Bubble = styled.div<{ $role: Role }>`
  max-width: 680px;
  padding: 14px 18px;
  border-radius: 12px;
  font-size: 15px;
  line-height: 1.55;
  white-space: pre-wrap;
  word-wrap: break-word;
  ${(p) =>
    p.$role === 'user'
      ? css`
          align-self: flex-end;
          background: ${DARK};
          color: ${LIGHT};
          border-bottom-right-radius: 4px;
        `
      : css`
          align-self: flex-start;
          background: #fff;
          color: ${DARK};
          border: 1px solid rgba(0, 0, 0, 0.08);
          border-bottom-left-radius: 4px;
        `}
`;

const AssistantFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  opacity: 0.7;
  font-size: 12px;
`;

const VoiceButton = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: ${GOLD};
  cursor: pointer;
  padding: 4px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: inherit;
  font-size: 12px;
  &:hover {
    color: ${GOLD_HOVER};
    background: rgba(198, 167, 94, 0.08);
  }
`;

const InputBar = styled.form`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  width: min(820px, calc(100% - 48px));
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  padding: 10px 12px 10px 18px;
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.12);
`;

const TextInput = styled.input`
  flex: 1;
  appearance: none;
  border: none;
  outline: none;
  background: transparent;
  font-family: "Inter", system-ui, sans-serif;
  font-size: 15px;
  color: ${DARK};
  padding: 8px 4px;
  &::placeholder {
    color: rgba(26, 26, 26, 0.4);
  }
`;

const MicButton = styled.button<{ $active: boolean }>`
  appearance: none;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${(p) => (p.$active ? GOLD : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : 'rgba(26,26,26,0.55)')};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms ease, color 120ms ease;
  ${(p) =>
    p.$active
      ? css`
          animation: ${pulseGold} 1s ease-in-out infinite;
        `
      : css`
          &:hover {
            color: ${GOLD};
            background: rgba(198, 167, 94, 0.12);
          }
        `}
`;

const SendButton = styled.button`
  appearance: none;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${GOLD};
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 120ms ease;
  &:hover {
    background: ${GOLD_HOVER};
  }
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const WaveformWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-right: 8px;
  height: 28px;
`;

const Bar = styled.span<{ $anim: ReturnType<typeof keyframes> }>`
  display: inline-block;
  width: 3px;
  background: ${GOLD};
  border-radius: 2px;
  animation: ${(p) => p.$anim} 900ms ease-in-out infinite;
`;

const Waveform: React.FC = () => (
  <WaveformWrap aria-hidden>
    <Bar $anim={bar1} style={{ animationDelay: '0ms' }} />
    <Bar $anim={bar2} style={{ animationDelay: '80ms' }} />
    <Bar $anim={bar3} style={{ animationDelay: '160ms' }} />
    <Bar $anim={bar4} style={{ animationDelay: '240ms' }} />
    <Bar $anim={bar5} style={{ animationDelay: '320ms' }} />
  </WaveformWrap>
);

/* ------------------------------------------------------------------------- */
/* Main component                                                             */
/* ------------------------------------------------------------------------- */

export const MoonshotVoiceChat: React.FC = () => {
  const mock = useDemoMock();
  const fixture = journeyFixtures[mock.journey] || journeyFixtures[1];

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [listening, setListening] = useState<boolean>(false);
  const [replaying, setReplaying] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const cancelRef = useRef<{ cancelled: boolean }>({ cancelled: false });

  // Transcript scrolling (2026-08 scroll redesign): same shared
  // pinned-to-bottom controller as every other chat surface — streaming
  // sticks only while the reader is at the bottom; scrolling up to re-read
  // is never yanked back down. Per-journey context keys land each demo
  // journey cleanly at the bottom.
  const messagesRef = useRef<HTMLDivElement>(null);
  const messagesContentRef = useRef<HTMLDivElement>(null);
  const { pinToBottom } = useTranscriptScroll({
    containerRef: messagesRef,
    contentRef: messagesContentRef,
    items: messages,
    getContextKey: () => `moonshot-${mock.journey}`,
    isOpen: true,
  });

  /** Reset conversation whenever the journey changes. */
  useEffect(() => {
    cancelRef.current.cancelled = true;
    cancelRef.current = { cancelled: false };
    setMessages([]);
    setInputValue('');
    setListening(false);
    setReplaying(false);
  }, [mock.journey]);

  /** Stream the fixture's assistant reply into a single assistant message. */
  const streamAssistantReply = useCallback(async () => {
    const token = cancelRef.current;
    const assistantId = `a-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setMessages((prev) => [
      ...prev,
      { id: assistantId, role: 'assistant', text: '', streaming: true, voiceUrl: fixture.voiceReplyUrl },
    ]);

    // 200ms "thinking" pause before the first token.
    await new Promise((r) => setTimeout(r, 200));
    if (token.cancelled) return;

    let stepIdx = 0;
    for (const chunk of fixture.chunks) {
      if (token.cancelled) return;
      const delay = chunk.delayMs ?? 40;
      if (delay > 0) await new Promise((r) => setTimeout(r, delay));
      if (token.cancelled) return;

      if (chunk.event === 'thinking') {
        // Skip rendering thinking events in the bubble — HUD already shows state.
        stepIdx += 1;
        mock.setStep(Math.min(stepIdx + 1, fixture.steps.length));
        continue;
      }

      if (chunk.delta) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, text: m.text + chunk.delta } : m,
          ),
        );
      }
      // Metadata-bearing events (citations, pdf_ready, etc.) just advance the HUD.
      if (chunk.event && chunk.event !== 'delta') {
        stepIdx += 1;
        mock.setStep(Math.min(stepIdx + 1, fixture.steps.length));
      }
    }

    setMessages((prev) =>
      prev.map((m) => (m.id === assistantId ? { ...m, streaming: false } : m)),
    );
  }, [fixture, mock]);

  /** Full scripted mic-flow: listen → transcribe → submit → stream reply. */
  const runVoiceFlow = useCallback(async () => {
    if (listening || replaying) return;
    cancelRef.current = { cancelled: false };
    const token = cancelRef.current;
    setListening(true);
    setInputValue('');
    mock.setStep(1);

    // 200ms initial listening state.
    await new Promise((r) => setTimeout(r, 200));
    if (token.cancelled) return;

    // Stream the user query into the input one word at a time (120ms each).
    const words = fixture.userQuery.split(/(\s+)/).filter((s) => s.length > 0);
    let acc = '';
    for (const w of words) {
      if (token.cancelled) return;
      acc += w;
      setInputValue(acc);
      await new Promise((r) => setTimeout(r, 120));
    }

    setListening(false);
    mock.setStep(2);

    // 200ms settle, then "submit" the user message and start the assistant stream.
    await new Promise((r) => setTimeout(r, 200));
    if (token.cancelled) return;

    const userId = `u-${Date.now()}`;
    setMessages((prev) => [...prev, { id: userId, role: 'user', text: fixture.userQuery }]);
    setInputValue('');
    // Sending always re-pins the transcript (2026-08 scroll redesign).
    pinToBottom('smooth');

    await streamAssistantReply();
  }, [fixture, listening, replaying, mock, streamAssistantReply]);

  /** Manual send handler (production mode + fallback). */
  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      const text = inputValue.trim();
      if (!text) return;
      const userId = `u-${Date.now()}`;
      setMessages((prev) => [...prev, { id: userId, role: 'user', text }]);
      setInputValue('');
      pinToBottom('smooth');
      if (mock.enabled) {
        await streamAssistantReply();
      }
    },
    [inputValue, mock.enabled, streamAssistantReply],
  );

  const handleMicClick = useCallback(() => {
    if (!mock.enabled) {
      // Production path: this is a no-op per PRE-158 spec until voice backend is wired.
      return;
    }
    runVoiceFlow().catch((err) => {
       
      console.warn('[moonshot] voice flow failed:', err);
      setListening(false);
    });
  }, [mock.enabled, runVoiceFlow]);

  /** Auto-play the voice reply on journey 1 landing (first load). */
  const autoPlayRef = useRef(false);
  useEffect(() => {
    if (!mock.enabled) return;
    if (autoPlayRef.current) return;
    if (mock.journey !== 1) return;
    autoPlayRef.current = true;
    // Kick off the scripted flow once the UI is mounted.
    const t = setTimeout(() => {
      runVoiceFlow().catch(() => undefined);
    }, 400);
    return () => clearTimeout(t);
  }, [mock.enabled, mock.journey, runVoiceFlow]);

  const handleReplayKey = useCallback(() => {
    cancelRef.current.cancelled = true;
    cancelRef.current = { cancelled: false };
    setReplaying(true);
    setMessages([]);
    setInputValue('');
    setTimeout(() => {
      setReplaying(false);
      runVoiceFlow().catch(() => undefined);
    }, 200);
  }, [runVoiceFlow]);

  /** K = replay in the provider fires setStep(1) via replayJourney — we also wipe UI. */
  useEffect(() => {
    if (!mock.enabled) return;
    const h = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && !(e.metaKey || e.ctrlKey || e.altKey)) {
        const tgt = e.target as HTMLElement | null;
        if (tgt) {
          const tag = (tgt.tagName || '').toLowerCase();
          if (tag === 'input' || tag === 'textarea' || tgt.isContentEditable) return;
        }
        handleReplayKey();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [mock.enabled, handleReplayKey]);

  const placeholder = useMemo(() => {
    if (listening) return 'Listening...';
    return mock.enabled
      ? 'Tap the mic to start the demo...'
      : 'Ask Prest0n anything...';
  }, [listening, mock.enabled]);

  const playVoiceReply = useCallback((url?: string) => {
    if (!url) return;
    try {
      const audio = new Audio(url);
      audio.play().catch(() => undefined);
    } catch {
      // ignore
    }
  }, []);

  return (
    <ChatShell>
      <div style={{flexShrink:0, display:"flex", flexDirection:"column", width:"600px", height:"100%", borderRight:"1px solid #eaeaea", background:"white"}}>
      <ChatHeader>
        <ChatTitle>Prest0n</ChatTitle>
        <ChatSubtitle>
          {mock.enabled
            ? `Moonshot demo · Journey ${mock.journey}/6 · ${mock.journeyName}`
            : 'Bilingual legal operations partner'}
        </ChatSubtitle>
      </ChatHeader>

      <Messages ref={messagesRef}>
        <MessagesContent ref={messagesContentRef}>
        {messages.map((m) => (
          <Bubble key={m.id} $role={m.role}>
            {m.text}
            {m.role === 'assistant' && !m.streaming && m.voiceUrl && (
              <AssistantFooter>
                <VoiceButton
                  type="button"
                  aria-label="Play voice reply"
                  onClick={() => playVoiceReply(m.voiceUrl)}
                >
                  <Volume2 size={14} />
                  <span>Play voice reply</span>
                </VoiceButton>
              </AssistantFooter>
            )}
          </Bubble>
        ))}
        </MessagesContent>
      </Messages>

      <InputBar onSubmit={handleSubmit} aria-label="Prest0n chat input">
        {listening && <Waveform />}
        <TextInput
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder={placeholder}
          aria-label="Message Prest0n"
          disabled={listening}
        />
        <MicButton
          type="button"
          $active={listening}
          onClick={handleMicClick}
          aria-label={listening ? 'Listening' : 'Start voice input'}
          aria-pressed={listening}
        >
          <Mic size={18} />
        </MicButton>
        <SendButton type="submit" disabled={!inputValue.trim() || listening} aria-label="Send message">
          <Send size={16} />
        </SendButton>
      </InputBar>
    </div>
      {(() => { switch (mock.journey) { case 1: return <BrowserUsePanel />; case 2: return <DeepResearchPanel />; case 3: return <DocumentReaderPanel />; case 4: return <LegalDraftPanel />; case 5: return <BilingualPanel />; case 6: return <VoicePanel />; case 7: return <AgentPanel />; case 8: return <MemoryPanel />; case 9: return <MarketingPanel />; default: return <div style={{flex: 1}}/>; } })()}
    </ChatShell>
  );
};

export default MoonshotVoiceChat;
